import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Card, CardBody } from "../../components/ui";
import {
  FaUsers, FaArrowRight, FaArrowLeft, FaCopy, FaDownload,
  FaSyncAlt, FaSearch, FaExclamationTriangle, FaCheckCircle,
  FaChevronRight, FaShieldAlt, FaUserTimes
} from "react-icons/fa";

export default function GroupExportGuide() {
  useEffect(() => {
    document.title = "Group Member Export Guide | Walytic - Export WhatsApp Group Contacts";
  }, []);

  return (
    <div className="page-content">
      <PageHeader title="Group Member Export Guide" subtitle="Browse your WhatsApp groups, view every member, and export phone numbers to Excel or clipboard." />

      <div className="max-w-4xl mx-auto">

        {/* Table of Contents */}
        <nav className="bg-surface-card rounded-xl border border-surface-border p-6 mb-12">
          <h2 className="font-semibold text-lg text-ink-primary mb-4">In this guide</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { id: 'groups-list', label: 'Groups list view' },
              { id: 'members-view', label: 'Members view' },
              { id: 'copy-numbers', label: 'Copy numbers to clipboard' },
              { id: 'export-excel', label: 'Export to Excel' },
              { id: 'lid-warning', label: 'Understanding unresolved LIDs' },
              { id: 'tips', label: 'Tips' },
            ].map((item) => (
              <a key={item.id} href={`#${item.id}`} className="flex items-center text-sm text-ink-secondary hover:text-brand py-1.5 transition-colors">
                <FaChevronRight className="h-4 w-4 mr-1 text-ink-muted" />
                {item.label}
              </a>
            ))}
          </div>
        </nav>

        {/* Section: Groups List */}
        <section id="groups-list" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Groups list view</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Open <strong>Group Export</strong> from the sidebar and select a connected WhatsApp session.
            Walytic fetches all groups that session belongs to and displays them in a list.
          </p>

          <div className="bg-surface-card rounded-xl border border-surface-border p-6 mb-6">
            <p className="text-sm font-medium text-ink-primary mb-3">Each group card shows:</p>
            <ul className="space-y-2 text-ink-secondary text-sm">
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Group name</strong> as it appears in WhatsApp</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Member count</strong> -- total participants in the group</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Badges</strong> for Community groups and Announce-only groups</span>
              </li>
            </ul>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-surface-card border border-surface-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaSearch className="h-4 w-4 text-ink-muted" />
                <h4 className="font-semibold text-sm text-ink-primary">Search</h4>
              </div>
              <p className="text-sm text-ink-secondary">Filter groups by name using the search bar at the top.</p>
            </div>
            <div className="bg-surface-card border border-surface-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaDownload className="h-4 w-4 text-brand" />
                <h4 className="font-semibold text-sm text-ink-primary">Export All Groups</h4>
              </div>
              <p className="text-sm text-ink-secondary">Downloads a summary Excel file listing every group, its member count, and type.</p>
            </div>
            <div className="bg-surface-card border border-surface-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaSyncAlt className="h-4 w-4 text-ink-muted" />
                <h4 className="font-semibold text-sm text-ink-primary">Refresh</h4>
              </div>
              <p className="text-sm text-ink-secondary">Re-fetches the group list from WhatsApp. Use this if you recently joined or left a group.</p>
            </div>
          </div>
        </section>

        {/* Section: Members View */}
        <section id="members-view" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Members view</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Click any group to open its member list. At the top, you see three counters:
          </p>

          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-surface-card border border-surface-border rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-ink-primary">247</p>
              <p className="text-sm text-ink-muted">Total Members</p>
            </div>
            <div className="bg-surface-card border border-surface-border rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-brand">5</p>
              <p className="text-sm text-ink-muted">Admins</p>
            </div>
            <div className="bg-surface-card border border-surface-border rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">12</p>
              <p className="text-sm text-ink-muted">Unresolved</p>
            </div>
          </div>

          <p className="text-ink-secondary leading-relaxed">
            Below the counters is a table of every member. Each row shows the phone number (or LID if unresolved),
            whether they are an admin, and their status. You can sort by any column.
          </p>
        </section>

        {/* Section: Copy Numbers */}
        <section id="copy-numbers" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Copy numbers to clipboard</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Click the <strong>&quot;Copy Numbers&quot;</strong> button above the member table. Walytic copies all
            resolved phone numbers to your clipboard, one per line. Unresolved LIDs are skipped automatically.
          </p>
          <p className="text-ink-secondary leading-relaxed">
            Paste the numbers directly into the Flow Builder loop node, or save them in a text file
            for later use in Send via Excel.
          </p>
        </section>

        {/* Section: Export Excel */}
        <section id="export-excel" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Export to Excel</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Click <strong>&quot;Export Excel&quot;</strong> to download a <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">.xlsx</code> file
            with the following columns:
          </p>

          <div className="bg-surface-card rounded-xl border border-surface-border overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-ink-primary">Column</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-primary">Description</th>
                </tr>
              </thead>
              <tbody className="text-ink-secondary">
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">#</td>
                  <td className="py-3 px-4">Row number</td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Phone Number</td>
                  <td className="py-3 px-4">The member&apos;s phone number (or &quot;Unresolved&quot; for LIDs)</td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Admin</td>
                  <td className="py-3 px-4">Yes or No</td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Status</td>
                  <td className="py-3 px-4">Resolved or Unresolved</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section: LID Warning */}
        <section id="lid-warning" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Understanding unresolved LIDs</h2>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3">
              <FaExclamationTriangle className="h-6 w-6 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-900 mb-2">What are LIDs?</p>
                <p className="text-amber-800 mb-3">
                  WhatsApp sometimes assigns internal &quot;LID&quot; identifiers to group members instead
                  of exposing their real phone number. This happens when you have never exchanged a
                  direct message with that person.
                </p>
                <p className="text-amber-800">
                  These members appear as <strong>&quot;Unresolved&quot;</strong> in the export. Once you send or
                  receive a message from them (even in the group), WhatsApp resolves the LID to their
                  actual phone number. At that point, refreshing the group data in Walytic will show
                  the real number.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Tips */}
        <section id="tips" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-6">Tips</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Refresh after group changes', text: 'If members joined or left recently, hit Refresh to pull the latest data from WhatsApp.' },
              { title: 'Combine with Number Verification', text: 'Export group members, then run the file through Number Verification to confirm which numbers are still active.' },
              { title: 'Large groups take a moment', text: 'Groups with 500+ members may take a few seconds to load. The member list streams in as data arrives.' },
              { title: 'Admin-only export', text: 'After exporting, filter the Excel file by the Admin column to quickly find group admins for outreach.' },
            ].map((tip, i) => (
              <div key={i} className="bg-surface-card border border-surface-border rounded-lg p-4">
                <h4 className="font-semibold text-sm text-ink-primary mb-1">{tip.title}</h4>
                <p className="text-sm text-ink-secondary">{tip.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Prev / Next */}
        <div className="flex items-center justify-between border-t border-surface-border pt-8">
          <Link to="/guide/number-verification" className="text-sm text-ink-muted hover:text-ink-primary flex items-center gap-1">
            <FaArrowLeft className="h-4 w-4" /> Number Verification
          </Link>
          <Link to="/guide/image-to-url" className="text-sm font-medium text-brand hover:opacity-80 flex items-center gap-1">
            Next: Image to URL <FaArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
